import { hashKey } from '@tanstack/react-query';
import { Atom, atom, Getter, Setter, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithQuery } from 'jotai-tanstack-query';
import { useMemo } from 'react';
import { activeAccountAtom } from 'src/contexts/AppContext/AppContext.atom';
import {
  InsertSellerEventConfirmationDialogSuppressionRequest,
  SellerEventConfirmationDialogSuppression,
  SellerUserClient,
} from 'src/WebApiController';

import { getConfirmationDialogTypeFromKey } from './useEventConfirmationDialogSuppressions';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// --- All requested eventIds ---
const requestedEventVirtualIdsAtom = atom<Set<string>>(new Set<string>());

// --- Debounce state stored in atoms instead of module-level variables ---
const pendingBatchAtom = atom<string[]>([]);
const debounceTimerAtom = atom<ReturnType<typeof setTimeout> | null>(null);

export const addRequestedEventIdsAtom = atom(
  null,
  (
    get,
    set,
    { eventIds, enabled }: { eventIds: string | string[]; enabled?: boolean }
  ) => {
    if (enabled === false) return;
    const ids = Array.isArray(eventIds) ? eventIds : [eventIds];

    // Append to pending batch
    const batch = [...get(pendingBatchAtom), ...ids];
    set(pendingBatchAtom, batch);

    // Reset debounce timer
    const existingTimer = get(debounceTimerAtom);
    if (existingTimer != null) clearTimeout(existingTimer);

    const timer = setTimeout(() => {
      set(debounceTimerAtom, null);
      const batchToFlush = get(pendingBatchAtom);
      set(pendingBatchAtom, []);

      const current = get(requestedEventVirtualIdsAtom);
      const newIds = batchToFlush.filter((id) => !current.has(id));
      if (newIds.length > 0) {
        set(requestedEventVirtualIdsAtom, new Set([...current, ...newIds]));
      }
    }, 100);
    set(debounceTimerAtom, timer);
  }
);

// --- Single query: fetches ALL requested eventIds, refreshes every 5 min ---
export const eventSuppressionsQueryAtom = atomWithQuery((get) => {
  const config = get(activeAccountAtom);
  const allEventIds = get(requestedEventVirtualIdsAtom);
  const sortedEventIds = Array.from(allEventIds).toSorted();

  const shouldQuery =
    config.webClientConfig?.activeAccountId != null &&
    sortedEventIds.length > 0;

  return {
    queryKey: [
      'eventConfirmationDialogSuppressions',
      config.webClientConfig?.activeAccountId,
      hashKey(sortedEventIds),
    ],
    queryFn: async (): Promise<SellerEventConfirmationDialogSuppression[]> => {
      if (!shouldQuery || !config.webClientConfig) return [];
      return new SellerUserClient(
        config.webClientConfig
      ).getSellerEventConfirmationDialogSuppressionsByEventIds(sortedEventIds);
    },
    enabled: shouldQuery,
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: REFRESH_INTERVAL_MS,
    placeholderData: (
      prev: SellerEventConfirmationDialogSuppression[] | undefined
    ) => prev ?? [],
  };
});

// --- Write atom: insert a suppression then invalidate the query ---
export const insertSuppressionAtom = atom(
  null,
  async (
    get: Getter,
    _set: Setter,
    request: InsertSellerEventConfirmationDialogSuppressionRequest
  ) => {
    const config = get(activeAccountAtom);
    const queryResult = get(eventSuppressionsQueryAtom);
    if (!config.webClientConfig) return;

    const client = new SellerUserClient(config.webClientConfig);
    const result =
      await client.insertSellerEventConfirmationDialogSuppression(request);

    if (result.succeeded) {
      queryResult.refetch();
    }

    return result;
  }
);

export const eventDialogSuppressionFamily = atomFamily<
  string | undefined,
  Atom<SellerEventConfirmationDialogSuppression[]>
>((eventId) =>
  atom((get) => {
    if (!eventId) return [];
    const queryResult = get(eventSuppressionsQueryAtom);
    const data = queryResult.data ?? [];
    return data.filter((s) => s.eventVirtualId === eventId);
  })
);

// --- Two-layer selector: eventId + dialogType → single suppression ---
export const useEventDialogSuppression = (
  eventId: string | undefined,
  key: string
) => {
  const suppressionAtom = eventDialogSuppressionFamily(eventId);
  const suppression = useAtomValue(suppressionAtom);
  return useMemo(() => {
    const dialogType = getConfirmationDialogTypeFromKey(key);
    if (!dialogType) {
      return null;
    }
    return (
      suppression.find((s) => s.confirmationDialogType === dialogType) ?? null
    );
  }, [key, suppression]);
};

export const dialogSuppressionByEventIdAtom = atom((get) => {
  // Subscribe to the query at read time so jotai tracks the dependency
  const queryResult = get(eventSuppressionsQueryAtom);
  const data = queryResult.data ?? [];

  // Return a lookup function that uses the captured data
  return (eventId: string) =>
    data
      .filter((s) => s.eventVirtualId === eventId)
      .reduce(
        (acc, suppression) => {
          acc[suppression.confirmationDialogType] = suppression;
          return acc;
        },
        {} as Record<string, SellerEventConfirmationDialogSuppression>
      );
});
